import React, { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { 
  DollarSign, TrendingUp, TrendingDown, Eye, Video, Globe, 
  BarChart3, PieChart, Settings, RefreshCw, 
  Info, Clock, Loader2, Save, RotateCcw, Play, Target,
  Banknote, Calculator, ChartLine, Coins, Trash2, SkipForward, 
  CheckCircle, MousePointerClick, ExternalLink
} from 'lucide-react';
import {
  AreaChart, Area,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart as RechartsPieChart, Pie, Cell
} from 'recharts';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL || '';

const CHART_COLORS = ['#10b981', '#3b82f6', '#8b5cf6', '#f59e0b', '#ef4444', '#ec4899', '#14b8a6', '#6366f1'];

const RevenueTab = ({ authToken }) => {
  const [loading, setLoading] = useState(true);
  const [currency, setCurrency] = useState('gbp');
  const [timeRange, setTimeRange] = useState(30);
  const [dashboardData, setDashboardData] = useState(null);
  const [projections, setProjections] = useState(null);
  const [configData, setConfigData] = useState(null);
  const [showSettings, setShowSettings] = useState(false);
  const [savingConfig, setSavingConfig] = useState(false);
  const [editedRates, setEditedRates] = useState({});
  const [adSubTab, setAdSubTab] = useState('google'); // 'google' or 'myads'
  const [myAdsData, setMyAdsData] = useState(null);
  const [myAdsLoading, setMyAdsLoading] = useState(false);
  
  // Fetch revenue dashboard data with retry
  const fetchDashboard = async (retryCount = 0) => {
    setLoading(true);
    try {
      const token = authToken || localStorage.getItem('adminToken') || localStorage.getItem('livestreamToken');
      if (!token) {
        console.warn('[Revenue] No auth token available');
        setLoading(false);
        return;
      }
      const headers = { Authorization: `Bearer ${token}` };
      const [dashRes, projRes, configRes] = await Promise.all([
        axios.get(`${API}/api/vod-admin/revenue/dashboard?currency=${currency}&days=${timeRange}`, { headers, timeout: 15000 }),
        axios.get(`${API}/api/vod-admin/revenue/projections?currency=${currency}`, { headers, timeout: 15000 }),
        axios.get(`${API}/api/vod-admin/revenue/config`, { headers, timeout: 15000 })
      ]);
      
      setDashboardData(dashRes.data);
      setProjections(projRes.data);
      setConfigData(configRes.data);
    } catch (err) {
      console.error('Error fetching revenue data:', err);
      // Retry up to 2 times on failure
      if (retryCount < 2) {
        console.log(`[Revenue] Retrying fetch (attempt ${retryCount + 2})...`);
        setTimeout(() => fetchDashboard(retryCount + 1), 1500);
        return;
      }
    } finally {
      setLoading(false);
    }
  };

  // Fetch My Ads data with retry
  const fetchMyAds = async (retryCount = 0) => {
    setMyAdsLoading(true);
    try {
      const token = authToken || localStorage.getItem('adminToken') || localStorage.getItem('livestreamToken');
      if (!token) {
        setMyAdsLoading(false);
        return;
      }
      const headers = { Authorization: `Bearer ${token}` };
      const res = await axios.get(`${API}/api/vod-admin/revenue/my-ads?days=${timeRange}`, { headers, timeout: 15000 });
      setMyAdsData(res.data);
    } catch (err) {
      console.error('Error fetching my ads data:', err);
      if (retryCount < 2) {
        setTimeout(() => fetchMyAds(retryCount + 1), 1500);
        return;
      }
    } finally {
      setMyAdsLoading(false);
    }
  };
  
  useEffect(() => {
    const token = authToken || localStorage.getItem('adminToken') || localStorage.getItem('livestreamToken');
    if (token) {
      fetchDashboard();
      fetchMyAds();
    }
  }, [authToken, currency, timeRange]);
  
  // Reset My Ads analytics
  const resetMyAds = async () => {
    if (!window.confirm('Reset ALL My Ads analytics? This will clear all impression, click, skip, and completion data for your custom ads. This cannot be undone.')) return;
    try {
      const token = authToken || localStorage.getItem('adminToken') || localStorage.getItem('livestreamToken');
      await axios.post(`${API}/api/vod-admin/revenue/reset-my-ads`, {}, { headers: { Authorization: `Bearer ${token}` } });
      alert('My Ads analytics have been reset!');
      fetchMyAds();
    } catch (err) {
      console.error('Error resetting My Ads:', err);
      alert('Failed to reset My Ads analytics');
    }
  };

  // Reset Google Ads analytics
  const resetGoogleAds = async () => {
    if (!window.confirm('Reset ALL Google Ads analytics? This will clear all Google/VAST ad impression data. This cannot be undone.')) return;
    try {
      const token = authToken || localStorage.getItem('adminToken') || localStorage.getItem('livestreamToken');
      await axios.post(`${API}/api/vod-admin/revenue/reset-google-ads`, {}, { headers: { Authorization: `Bearer ${token}` } });
      alert('Google Ads analytics have been reset!');
      fetchMyAds();
      fetchDashboard();
    } catch (err) {
      console.error('Error resetting Google Ads:', err);
      alert('Failed to reset Google Ads analytics');
    }
  };
  
  // Handle RPM rate edit
  const handleRateEdit = (category, tier, field, value) => {
    const key = `${category}_${tier}`;
    setEditedRates(prev => ({
      ...prev,
      [key]: {
        ...prev[key],
        content_category: category,
        country_group: tier,
        [field]: parseFloat(value) || 0
      }
    }));
  };
  
  // Save edited rates
  const saveRates = async () => {
    setSavingConfig(true);
    try {
      const headers = { Authorization: `Bearer ${authToken}` };
      
      for (const key of Object.keys(editedRates)) {
        const rate = editedRates[key];
        const original = configData?.rpm_rates?.find(
          r => r.content_category === rate.content_category && r.country_group === rate.country_group
        ) || {};
        
        await axios.post(`${API}/api/vod-admin/revenue/config/rpm`, {
          content_category: rate.content_category,
          country_group: rate.country_group,
          rpm_gbp: rate.rpm_gbp ?? original.rpm_gbp ?? 1.0,
          rpm_usd: rate.rpm_usd ?? original.rpm_usd ?? 1.25
        }, { headers });
      }
      
      setEditedRates({});
      await fetchDashboard();
      alert('RPM rates saved successfully!');
    } catch (err) {
      console.error('Error saving rates:', err);
      alert('Failed to save rates');
    } finally {
      setSavingConfig(false);
    }
  };
  
  // Reset to defaults
  const resetToDefaults = async () => {
    if (!window.confirm('Reset all RPM rates to default values?')) return;
    
    setSavingConfig(true);
    try {
      const headers = { Authorization: `Bearer ${authToken}` };
      await axios.post(`${API}/api/vod-admin/revenue/config/reset-defaults`, {}, { headers });
      setEditedRates({});
      await fetchDashboard();
      alert('Reset to default rates!');
    } catch (err) {
      console.error('Error resetting rates:', err);
    } finally {
      setSavingConfig(false);
    }
  };
  
  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-green-500" />
      </div>
    );
  }
  
  const { summary, top_earning_videos, revenue_by_category, revenue_by_country, daily_revenue } = dashboardData || {};
  const currencySymbol = currency === 'gbp' ? '£' : '$';
  
  // Prepare chart data
  const dailyRevenueData = daily_revenue?.map(d => ({
    date: new Date(d.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
    revenue: d.revenue,
    views: d.views
  })) || [];
  
  const categoryPieData = revenue_by_category?.map((c, i) => ({
    name: c.category,
    value: c.revenue,
    color: CHART_COLORS[i % CHART_COLORS.length]
  })) || [];
  
  // Calculate totals for percentages
  const totalCategoryRevenue = categoryPieData.reduce((a, b) => a + b.value, 0);
  const totalCountryRevenue = revenue_by_country?.reduce((a, b) => a + b.revenue, 0) || 0;
  
  return (
    <div className="space-y-6">
      {/* Header Controls */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          {/* Currency Toggle */}
          <div className="flex bg-gray-800 rounded-lg p-1">
            <button
              onClick={() => setCurrency('gbp')}
              className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                currency === 'gbp' ? 'bg-green-600 text-white' : 'text-gray-400 hover:text-white'
              }`}
            >
              £ GBP
            </button>
            <button
              onClick={() => setCurrency('usd')}
              className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                currency === 'usd' ? 'bg-green-600 text-white' : 'text-gray-400 hover:text-white'
              }`}
            >
              $ USD
            </button>
          </div>
          
          {/* Time Range */}
          <select
            value={timeRange}
            onChange={(e) => setTimeRange(parseInt(e.target.value))}
            className="bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white"
          >
            <option value={7}>Last 7 days</option>
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
            <option value={365}>Last year</option>
          </select>
        </div>
        
        <div className="flex items-center gap-2">
          {/* Settings Toggle */}
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowSettings(!showSettings)}
            className="border-gray-700 text-gray-300"
          >
            <Settings className="h-4 w-4 mr-1" />
            RPM Settings
          </Button>
          
          {/* Refresh */}
          <Button
            variant="ghost"
            size="sm"
            onClick={fetchDashboard}
            className="text-gray-400"
          >
            <RefreshCw className="h-4 w-4" />
          </Button>
        </div>
      </div>
      
      {/* Ad Type Sub-Tabs */}
      <div className="flex bg-gray-800/50 rounded-lg p-1 gap-1" data-testid="ad-type-tabs">
        <button
          onClick={() => setAdSubTab('google')}
          className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors ${
            adSubTab === 'google' 
              ? 'bg-green-600 text-white shadow-lg shadow-green-600/20' 
              : 'text-gray-400 hover:text-white hover:bg-gray-700/50'
          }`}
          data-testid="google-ads-tab"
        >
          <Globe className="h-4 w-4" />
          Google Ads
        </button>
        <button
          onClick={() => setAdSubTab('myads')}
          className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors ${
            adSubTab === 'myads' 
              ? 'bg-purple-600 text-white shadow-lg shadow-purple-600/20' 
              : 'text-gray-400 hover:text-white hover:bg-gray-700/50'
          }`}
          data-testid="my-ads-tab"
        >
          <Play className="h-4 w-4" />
          My Ads
        </button>
      </div>

      {adSubTab === 'google' ? (
      <>
      {/* Check if Google ads have actually been served */}
      {(!myAdsData?.google_ads?.total_impressions) ? (
        <div className="bg-gray-900/50 rounded-xl border border-gray-800 p-12 text-center">
          <Globe className="h-16 w-16 mx-auto mb-4 text-gray-600" />
          <h3 className="text-xl font-semibold text-white mb-2">Google Ads Not Active Yet</h3>
          <p className="text-gray-400 max-w-md mx-auto mb-6">
            No Google ads have been served on your videos. Once your Google AdSense account is approved and ads start running, 
            revenue data will appear here automatically.
          </p>
          <div className="bg-yellow-900/20 border border-yellow-500/30 rounded-lg p-4 max-w-md mx-auto">
            <div className="flex items-start gap-2">
              <Info className="h-4 w-4 text-yellow-500 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-yellow-200/70 text-left">
                You can still configure RPM rates in Settings below so revenue is calculated accurately once ads go live.
              </p>
            </div>
          </div>
        </div>
      ) : (
      <>
      {/* AdSense Notice */}
      <div className="bg-yellow-900/20 border border-yellow-500/30 rounded-xl p-4 flex items-start gap-3">
        <Info className="h-5 w-5 text-yellow-500 flex-shrink-0 mt-0.5" />
        <div>
          <p className="text-yellow-300 font-medium">Estimated Revenue Only</p>
          <p className="text-yellow-200/70 text-sm mt-1">
            These figures are estimates based on industry-average RPM rates. Once your Google AdSense account is approved, 
            update the RPM values in settings to match your actual rates for more accurate projections.
          </p>
        </div>
      </div>
      
      {/* Summary Cards - Same style as Traffic Sources */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <div className="bg-gradient-to-br from-green-600/30 to-emerald-600/20 rounded-xl p-4 border border-green-500/30">
          <div className="flex items-center gap-2 mb-2">
            <DollarSign className="h-5 w-5 text-green-400" />
            <span className="text-gray-300 text-xs">Total Revenue</span>
          </div>
          <p className="text-2xl font-bold text-white">{currencySymbol}{summary?.total_estimated_revenue?.toFixed(2) || '0.00'}</p>
          <p className="text-xs text-green-300 mt-1">Last {timeRange} days</p>
        </div>
        
        <div className="bg-gradient-to-br from-blue-600/30 to-cyan-600/20 rounded-xl p-4 border border-blue-500/30">
          <div className="flex items-center gap-2 mb-2">
            <Eye className="h-5 w-5 text-blue-400" />
            <span className="text-gray-300 text-xs">Revenue/Visitor</span>
          </div>
          <p className="text-2xl font-bold text-white">{currencySymbol}{summary?.revenue_per_visitor?.toFixed(4) || '0.0000'}</p>
          <p className="text-xs text-blue-300 mt-1">{summary?.verified_views?.toLocaleString() || 0} verified views</p>
          <p className="text-xs text-blue-400/60 mt-0.5">{summary?.total_views?.toLocaleString() || 0} all-time</p>
        </div>
        
        <div className="bg-gradient-to-br from-purple-600/30 to-pink-600/20 rounded-xl p-4 border border-purple-500/30">
          <div className="flex items-center gap-2 mb-2">
            <BarChart3 className="h-5 w-5 text-purple-400" />
            <span className="text-gray-300 text-xs">Effective RPM</span>
          </div>
          <p className="text-2xl font-bold text-white">{currencySymbol}{summary?.effective_rpm?.toFixed(2) || '0.00'}</p>
          <p className="text-xs text-purple-300 mt-1">Per 1,000 views</p>
        </div>
        
        <div className="bg-gradient-to-br from-yellow-600/30 to-orange-600/20 rounded-xl p-4 border border-yellow-500/30">
          <div className="flex items-center gap-2 mb-2">
            <Banknote className="h-5 w-5 text-yellow-400" />
            <span className="text-gray-300 text-xs">Monthly Est.</span>
          </div>
          <p className="text-2xl font-bold text-white">{currencySymbol}{projections?.projections?.monthly?.toFixed(2) || '0.00'}</p>
          <p className="text-xs text-yellow-300 mt-1">Projected</p>
        </div>
        
        <div className="bg-gradient-to-br from-red-600/30 to-rose-600/20 rounded-xl p-4 border border-red-500/30">
          <div className="flex items-center gap-2 mb-2">
            <Coins className="h-5 w-5 text-red-400" />
            <span className="text-gray-300 text-xs">Quarterly Est.</span>
          </div>
          <p className="text-2xl font-bold text-white">{currencySymbol}{projections?.projections?.quarterly?.toFixed(2) || '0.00'}</p>
          <p className="text-xs text-red-300 mt-1">Projected</p>
        </div>
        
        <div className="bg-gradient-to-br from-pink-600/30 to-fuchsia-600/20 rounded-xl p-4 border border-pink-500/30">
          <div className="flex items-center gap-2 mb-2">
            <Calculator className="h-5 w-5 text-pink-400" />
            <span className="text-gray-300 text-xs">Yearly Est.</span>
          </div>
          <p className="text-2xl font-bold text-white">{currencySymbol}{projections?.projections?.yearly?.toFixed(2) || '0.00'}</p>
          <p className="text-xs text-pink-300 mt-1">Projected</p>
        </div>
      </div>

      {/* Main Content Grid - Same as Traffic Sources */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Revenue by Category - Pie Chart with Legend */}
        <div className="bg-gray-900 rounded-xl border border-gray-800 overflow-hidden">
          <div className="p-4 border-b border-gray-800">
            <h3 className="font-bold text-white flex items-center gap-2">
              <PieChart className="h-5 w-5 text-green-400" />
              Revenue by Category
            </h3>
          </div>
          <div className="p-6">
            {categoryPieData.length > 0 && totalCategoryRevenue > 0 ? (
              <div className="flex items-center gap-6">
                <div style={{ width: '150px', height: '150px', flexShrink: 0 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <RechartsPieChart>
                      <Pie
                        data={categoryPieData}
                        cx="50%"
                        cy="50%"
                        innerRadius={45}
                        outerRadius={70}
                        dataKey="value"
                        stroke="none"
                      >
                        {categoryPieData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                    </RechartsPieChart>
                  </ResponsiveContainer>
                </div>
                <div className="flex-1 space-y-3">
                  {categoryPieData.map((item, idx) => {
                    const pct = totalCategoryRevenue > 0 ? ((item.value / totalCategoryRevenue) * 100).toFixed(1) : 0;
                    return (
                      <div key={item.name} className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-4 h-4 rounded" style={{ backgroundColor: item.color }} />
                          <span className="text-sm text-gray-300">{item.name}</span>
                        </div>
                        <div className="flex items-center gap-4">
                          <span className="text-sm font-bold text-white">{currencySymbol}{item.value?.toFixed(2)}</span>
                          <span className="text-sm text-gray-400 w-16 text-right">{pct}%</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-center h-48 text-gray-500">
                <div className="text-center">
                  <PieChart className="h-12 w-12 mx-auto mb-2 opacity-50" />
                  <p className="text-gray-400">No category revenue data yet</p>
                  <p className="text-xs text-gray-500 mt-1">Revenue will appear as viewers watch your content</p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Revenue by Category - Progress Bars */}
        <div className="bg-gray-900 rounded-xl border border-gray-800 overflow-hidden">
          <div className="p-4 border-b border-gray-800">
            <h3 className="font-bold text-white flex items-center gap-2">
              <BarChart3 className="h-5 w-5 text-blue-400" />
              Category Breakdown
            </h3>
          </div>
          <div className="p-6 space-y-4">
            {revenue_by_category && revenue_by_category.length > 0 ? revenue_by_category.map((cat, idx) => {
              const pct = totalCategoryRevenue > 0 ? ((cat.revenue / totalCategoryRevenue) * 100).toFixed(1) : 0;
              return (
                <div key={cat.category}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <Video className="h-4 w-4" style={{ color: CHART_COLORS[idx % CHART_COLORS.length] }} />
                      <span className="text-sm text-gray-300">{cat.category}</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-sm font-bold text-white">{currencySymbol}{cat.revenue?.toFixed(2)}</span>
                      <span className="text-xs text-gray-400">({pct}%)</span>
                    </div>
                  </div>
                  <div className="h-2 bg-gray-800 rounded-full overflow-hidden">
                    <div 
                      className="h-full rounded-full transition-all" 
                      style={{ width: `${Math.max(parseFloat(pct), 2)}%`, backgroundColor: CHART_COLORS[idx % CHART_COLORS.length] }}
                    />
                  </div>
                </div>
              );
            }) : (
              <div className="text-center text-gray-500 py-8">
                <BarChart3 className="h-10 w-10 mx-auto mb-2 opacity-50" />
                <p className="text-gray-400">No category data</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Revenue Trend Chart */}
      <div className="bg-gray-900 rounded-xl border border-gray-800 overflow-hidden">
        <div className="p-4 border-b border-gray-800">
          <h3 className="font-bold text-white flex items-center gap-2">
            <ChartLine className="h-5 w-5 text-green-400" />
            Revenue Trend
          </h3>
          <p className="text-xs text-gray-500 mt-1">Daily estimated revenue over time</p>
        </div>
        <div className="p-6">
          {dailyRevenueData.length > 0 ? (
            <div style={{ height: 280 }}>
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={dailyRevenueData}>
                  <defs>
                    <linearGradient id="revenueGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#10b981" stopOpacity={0.4} />
                      <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#374151" vertical={false} />
                  <XAxis dataKey="date" stroke="#9ca3af" fontSize={11} />
                  <YAxis stroke="#9ca3af" fontSize={11} tickFormatter={(v) => `${currencySymbol}${v}`} />
                  <Tooltip
                    contentStyle={{ backgroundColor: '#1f2937', border: '1px solid #374151', borderRadius: '8px' }}
                    formatter={(value) => [`${currencySymbol}${value?.toFixed(4)}`, 'Revenue']}
                  />
                  <Area
                    type="monotone"
                    dataKey="revenue"
                    stroke="#10b981"
                    strokeWidth={2}
                    fill="url(#revenueGradient)"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="h-64 flex items-center justify-center text-gray-500">
              <div className="text-center">
                <ChartLine className="h-12 w-12 mx-auto mb-2 opacity-50" />
                <p className="text-gray-400">No revenue data for this period</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Top Earning Videos */}
      <div className="bg-gray-900 rounded-xl border border-gray-800 overflow-hidden">
        <div className="p-4 border-b border-gray-800">
          <h3 className="font-bold text-white flex items-center gap-2">
            <Video className="h-5 w-5 text-green-400" />
            Top Earning Videos
          </h3>
          <p className="text-xs text-gray-500 mt-1">Videos ranked by estimated revenue</p>
        </div>
        <div className="p-4">
          {top_earning_videos && top_earning_videos.length > 0 ? (
            <div className="space-y-3">
              {top_earning_videos.slice(0, 10).map((video, idx) => {
                const maxRevenue = top_earning_videos[0]?.[`estimated_revenue_${currency}`] || 1;
                const pct = ((video[`estimated_revenue_${currency}`] || 0) / maxRevenue) * 100;
                return (
                  <div key={video.video_id}>
                    <div className="flex items-center justify-between mb-1">
                      <div className="flex items-center gap-3 min-w-0 flex-1">
                        <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${
                          idx === 0 ? 'bg-yellow-500 text-black' :
                          idx === 1 ? 'bg-gray-400 text-black' :
                          idx === 2 ? 'bg-orange-600 text-white' :
                          'bg-gray-700 text-gray-300'
                        }`}>
                          {idx + 1}
                        </span>
                        {video.thumbnail_url && (
                          <img
                            src={video.thumbnail_url}
                            alt=""
                            className="w-10 h-7 rounded object-cover flex-shrink-0"
                          />
                        )}
                        <span className="text-sm text-gray-300 truncate">{video.title}</span>
                      </div>
                      <div className="flex items-center gap-4 flex-shrink-0">
                        <span className="text-xs text-gray-500">{video.views?.toLocaleString()} views</span>
                        <span className="text-xs text-gray-500">{video.avg_completion?.toFixed(0)}%</span>
                        <span className="text-sm font-bold text-green-400 w-20 text-right">
                          {currencySymbol}{video[`estimated_revenue_${currency}`]?.toFixed(2) || '0.00'}
                        </span>
                      </div>
                    </div>
                    <div className="h-1.5 bg-gray-800 rounded-full overflow-hidden ml-9">
                      <div 
                        className="h-full rounded-full transition-all bg-green-500" 
                        style={{ width: `${Math.max(pct, 2)}%` }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center text-gray-500 py-8">
              <Video className="h-10 w-10 mx-auto mb-2 opacity-50" />
              <p className="text-gray-400">No video revenue data yet</p>
              <p className="text-xs text-gray-500 mt-1">Revenue will appear as viewers watch your videos</p>
            </div>
          )}
        </div>
      </div>

      {/* Revenue by Country */}
      <div className="bg-gray-900 rounded-xl border border-gray-800 overflow-hidden">
        <div className="p-4 border-b border-gray-800">
          <h3 className="font-bold text-white flex items-center gap-2">
            <Globe className="h-5 w-5 text-blue-400" />
            Revenue by Country
          </h3>
          <p className="text-xs text-gray-500 mt-1">Geographic breakdown of estimated revenue</p>
        </div>
        <div className="p-4">
          {revenue_by_country && revenue_by_country.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {revenue_by_country.slice(0, 9).map((country, idx) => {
                const pct = totalCountryRevenue > 0 ? ((country.revenue / totalCountryRevenue) * 100).toFixed(1) : 0;
                return (
                  <div key={country.country || idx} className="bg-gray-800/50 rounded-lg p-3">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2 min-w-0">
                        <Globe className="h-4 w-4 text-blue-400 flex-shrink-0" />
                        <span className="text-sm text-gray-300 truncate">{country.country || 'Unknown'}</span>
                      </div>
                      <span className="text-sm font-bold text-green-400 ml-2">
                        {currencySymbol}{country.revenue?.toFixed(2)}
                      </span>
                    </div>
                    <div className="flex items-center justify-between text-xs text-gray-500">
                      <span>{country.views?.toLocaleString()} views</span>
                      <span>{pct}%</span>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center text-gray-500 py-8">
              <Globe className="h-10 w-10 mx-auto mb-2 opacity-50" />
              <p className="text-gray-400">No geographic data yet</p>
            </div>
          )}
        </div>
      </div>
      
      {/* RPM Settings Panel */}
      {showSettings && (
        <div className="bg-gray-900 rounded-xl border border-gray-800 overflow-hidden">
          <div className="p-4 border-b border-gray-800">
            <div className="flex items-center justify-between">
              <h3 className="font-bold text-white flex items-center gap-2">
                <Settings className="h-5 w-5 text-gray-400" />
                RPM Rate Configuration
              </h3>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={resetToDefaults}
                  disabled={savingConfig}
                  className="border-gray-700 text-gray-300"
                >
                  <RotateCcw className="h-4 w-4 mr-1" />
                  Reset Defaults
                </Button>
                {Object.keys(editedRates).length > 0 && (
                  <Button
                    size="sm"
                    onClick={saveRates}
                    disabled={savingConfig}
                    className="bg-green-600 hover:bg-green-500"
                  >
                    {savingConfig ? (
                      <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                    ) : (
                      <Save className="h-4 w-4 mr-1" />
                    )}
                    Save Changes
                  </Button>
                )}
              </div>
            </div>
            <p className="text-sm text-gray-400 mt-2">
              Adjust RPM (Revenue Per Mille - per 1,000 views) rates by content category and geographic tier.
              Update these values with your actual AdSense rates for accurate estimates.
            </p>
          </div>
          <div className="p-4 overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-800">
                  <th className="text-left p-3 text-gray-400 font-medium text-sm">Category</th>
                  <th className="text-center p-3 text-gray-400 font-medium text-sm" colSpan={2}>
                    Tier 1 (UK/US/CA/AU)
                  </th>
                  <th className="text-center p-3 text-gray-400 font-medium text-sm" colSpan={2}>
                    Tier 2 (EU/APAC)
                  </th>
                  <th className="text-center p-3 text-gray-400 font-medium text-sm" colSpan={2}>
                    Tier 3 (Other)
                  </th>
                </tr>
                <tr className="border-b border-gray-800">
                  <th></th>
                  <th className="text-center p-2 text-xs text-gray-500">GBP</th>
                  <th className="text-center p-2 text-xs text-gray-500">USD</th>
                  <th className="text-center p-2 text-xs text-gray-500">GBP</th>
                  <th className="text-center p-2 text-xs text-gray-500">USD</th>
                  <th className="text-center p-2 text-xs text-gray-500">GBP</th>
                  <th className="text-center p-2 text-xs text-gray-500">USD</th>
                </tr>
              </thead>
              <tbody>
                {configData?.content_categories?.map(category => {
                  const tier1 = configData.rpm_rates?.find(r => r.content_category === category && r.country_group === 'tier1') || {};
                  const tier2 = configData.rpm_rates?.find(r => r.content_category === category && r.country_group === 'tier2') || {};
                  const tier3 = configData.rpm_rates?.find(r => r.content_category === category && r.country_group === 'tier3') || {};
                  
                  return (
                    <tr key={category} className="border-b border-gray-800/50">
                      <td className="p-3 font-medium text-gray-300">{category}</td>
                      {/* Tier 1 */}
                      <td className="p-2">
                        <Input
                          type="number"
                          step="0.01"
                          defaultValue={tier1.rpm_gbp || 1.0}
                          onChange={(e) => handleRateEdit(category, 'tier1', 'rpm_gbp', e.target.value)}
                          className="bg-gray-800 border-gray-700 text-center w-20 text-white"
                        />
                      </td>
                      <td className="p-2">
                        <Input
                          type="number"
                          step="0.01"
                          defaultValue={tier1.rpm_usd || 1.25}
                          onChange={(e) => handleRateEdit(category, 'tier1', 'rpm_usd', e.target.value)}
                          className="bg-gray-800 border-gray-700 text-center w-20 text-white"
                        />
                      </td>
                      {/* Tier 2 */}
                      <td className="p-2">
                        <Input
                          type="number"
                          step="0.01"
                          defaultValue={tier2.rpm_gbp || 0.8}
                          onChange={(e) => handleRateEdit(category, 'tier2', 'rpm_gbp', e.target.value)}
                          className="bg-gray-800 border-gray-700 text-center w-20 text-white"
                        />
                      </td>
                      <td className="p-2">
                        <Input
                          type="number"
                          step="0.01"
                          defaultValue={tier2.rpm_usd || 1.0}
                          onChange={(e) => handleRateEdit(category, 'tier2', 'rpm_usd', e.target.value)}
                          className="bg-gray-800 border-gray-700 text-center w-20 text-white"
                        />
                      </td>
                      {/* Tier 3 */}
                      <td className="p-2">
                        <Input
                          type="number"
                          step="0.01"
                          defaultValue={tier3.rpm_gbp || 0.5}
                          onChange={(e) => handleRateEdit(category, 'tier3', 'rpm_gbp', e.target.value)}
                          className="bg-gray-800 border-gray-700 text-center w-20 text-white"
                        />
                      </td>
                      <td className="p-2">
                        <Input
                          type="number"
                          step="0.01"
                          defaultValue={tier3.rpm_usd || 0.62}
                          onChange={(e) => handleRateEdit(category, 'tier3', 'rpm_usd', e.target.value)}
                          className="bg-gray-800 border-gray-700 text-center w-20 text-white"
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          
          {/* Watch Time Multipliers Info */}
          <div className="p-4 border-t border-gray-800">
            <h4 className="font-medium text-white mb-3 flex items-center gap-2">
              <Clock className="h-4 w-4 text-purple-400" />
              Watch Time Multipliers (YouTube-style)
            </h4>
            <p className="text-sm text-gray-400 mb-4">
              Revenue is scaled based on how much of the video viewers watch. Higher engagement = higher revenue.
            </p>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
              {configData?.watch_multipliers?.map((m, idx) => (
                <div key={idx} className="bg-gray-800/50 rounded-lg p-3 text-center">
                  <p className="text-xs text-gray-400 mb-1">{m.min_completion}-{m.max_completion}%</p>
                  <p className="text-xl font-bold text-purple-400">{m.multiplier}x</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
      </>
      )}
      </>
      ) : (
      /* ════════════ MY ADS TAB ════════════ */
      <div className="space-y-6" data-testid="my-ads-panel">
        {myAdsLoading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="h-8 w-8 animate-spin text-purple-500" />
          </div>
        ) : !myAdsData ? (
          <div className="text-center py-20 text-gray-400">No ad data available</div>
        ) : (
          <>
            {/* My Ads Summary Cards */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              <div className="bg-gradient-to-br from-purple-600/20 to-purple-900/20 rounded-xl p-4 border border-purple-500/30">
                <div className="flex items-center gap-2 mb-1.5">
                  <Eye className="h-4 w-4 text-purple-400" />
                  <span className="text-gray-300 text-xs">Plays</span>
                </div>
                <p className="text-xl font-bold text-white">{(myAdsData.my_ads?.total_impressions || 0).toLocaleString()}</p>
                <p className="text-xs text-purple-300 mt-0.5">{myAdsData.my_ads?.total_ads || 0} ads</p>
              </div>
              <div className="bg-gradient-to-br from-green-600/20 to-green-900/20 rounded-xl p-4 border border-green-500/30">
                <div className="flex items-center gap-2 mb-1.5">
                  <MousePointerClick className="h-4 w-4 text-green-400" />
                  <span className="text-gray-300 text-xs">CTA Clicks</span>
                </div>
                <p className="text-xl font-bold text-white">{(myAdsData.my_ads?.total_clicks || 0).toLocaleString()}</p>
                <p className="text-xs text-green-300 mt-0.5">Potential leads</p>
              </div>
              <div className="bg-gradient-to-br from-orange-600/20 to-orange-900/20 rounded-xl p-4 border border-orange-500/30">
                <div className="flex items-center gap-2 mb-1.5">
                  <SkipForward className="h-4 w-4 text-orange-400" />
                  <span className="text-gray-300 text-xs">Skipped</span>
                </div>
                <p className="text-xl font-bold text-white">{(myAdsData.my_ads?.total_skips || 0).toLocaleString()}</p>
                <p className="text-xs text-orange-300 mt-0.5">
                  {myAdsData.my_ads?.total_impressions > 0 
                    ? `${Math.round(((myAdsData.my_ads?.total_skips || 0) / myAdsData.my_ads.total_impressions) * 100)}% skip rate` 
                    : 'No data'}
                </p>
              </div>
              <div className="bg-gradient-to-br from-cyan-600/20 to-cyan-900/20 rounded-xl p-4 border border-cyan-500/30">
                <div className="flex items-center gap-2 mb-1.5">
                  <CheckCircle className="h-4 w-4 text-cyan-400" />
                  <span className="text-gray-300 text-xs">Completed</span>
                </div>
                <p className="text-xl font-bold text-white">{(myAdsData.my_ads?.total_completions || 0).toLocaleString()}</p>
                <p className="text-xs text-cyan-300 mt-0.5">
                  {myAdsData.my_ads?.total_impressions > 0 
                    ? `${Math.round(((myAdsData.my_ads?.total_completions || 0) / myAdsData.my_ads.total_impressions) * 100)}% completion` 
                    : 'No data'}
                </p>
              </div>
              <div className="bg-gradient-to-br from-blue-600/20 to-blue-900/20 rounded-xl p-4 border border-blue-500/30">
                <div className="flex items-center gap-2 mb-1.5">
                  <Globe className="h-4 w-4 text-blue-400" />
                  <span className="text-gray-300 text-xs">Google Ads</span>
                </div>
                <p className="text-xl font-bold text-white">{(myAdsData.google_ads?.total_impressions || 0).toLocaleString()}</p>
                <p className="text-xs text-blue-300 mt-0.5">{myAdsData.google_ads?.videos?.length || 0} videos</p>
              </div>
            </div>

            {/* My Ads — Per Ad Breakdown */}
            <div className="bg-gray-900/50 rounded-xl border border-gray-800 overflow-hidden">
              <div className="p-4 border-b border-gray-800 flex items-center justify-between">
                <div>
                  <h3 className="text-white font-semibold flex items-center gap-2">
                    <Play className="h-5 w-5 text-purple-400" />
                    Your Video Ads — Per Ad Performance
                  </h3>
                  <p className="text-xs text-gray-400 mt-1">How each of your custom ads performed across videos</p>
                </div>
                <button
                  onClick={resetMyAds}
                  data-testid="reset-my-ads-btn"
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-red-500/10 hover:bg-red-500/20 border border-red-500/30 rounded-lg text-red-400 text-xs font-medium transition-colors"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                  Reset My Ads
                </button>
              </div>
              
              {myAdsData.my_ads?.ads?.length > 0 ? (
                <div className="divide-y divide-gray-800">
                  {myAdsData.my_ads.ads.map((ad, idx) => (
                    <div key={ad.id || idx} className="p-4">
                      <div className="flex items-center gap-3 mb-3">
                        {ad.thumbnail_url ? (
                          <img src={ad.thumbnail_url} alt="" className="w-16 h-10 object-cover rounded" />
                        ) : (
                          <div className="w-16 h-10 bg-purple-900/30 rounded flex items-center justify-center">
                            <Play className="h-4 w-4 text-purple-400" />
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <p className="text-white font-medium truncate">{ad.title}</p>
                          <div className="flex items-center gap-2 mt-1 flex-wrap">
                            <span className="inline-flex items-center gap-1 text-xs text-purple-300 bg-purple-500/10 px-2 py-0.5 rounded">
                              <Eye className="h-3 w-3" /> {(ad.period_impressions || 0).toLocaleString()} plays
                            </span>
                            <span className="inline-flex items-center gap-1 text-xs text-green-300 bg-green-500/10 px-2 py-0.5 rounded">
                              <MousePointerClick className="h-3 w-3" /> {(ad.total_clicks || 0).toLocaleString()} CTA clicks
                            </span>
                            <span className="inline-flex items-center gap-1 text-xs text-orange-300 bg-orange-500/10 px-2 py-0.5 rounded">
                              <SkipForward className="h-3 w-3" /> {(ad.total_skips || 0).toLocaleString()} skipped
                            </span>
                            <span className="inline-flex items-center gap-1 text-xs text-cyan-300 bg-cyan-500/10 px-2 py-0.5 rounded">
                              <CheckCircle className="h-3 w-3" /> {(ad.total_completions || 0).toLocaleString()} completed
                            </span>
                            <span className="inline-flex items-center gap-1 text-xs text-yellow-300 bg-yellow-500/10 px-2 py-0.5 rounded">
                              <Target className="h-3 w-3" /> CTR: {ad.ctr || 0}%
                            </span>
                          </div>
                        </div>
                        <span className={`text-xs px-2 py-0.5 rounded ${ad.is_active ? 'bg-green-500/20 text-green-300' : 'bg-gray-700 text-gray-400'}`}>
                          {ad.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                      
                      {/* Per-video breakdown for this ad */}
                      {ad.videos?.length > 0 && (
                        <div className="ml-4 space-y-1.5">
                          {ad.videos.map((v, vidx) => (
                            <div key={v.video_id || vidx} className="flex items-center gap-2 text-xs">
                              <div className="w-2 h-2 rounded-full bg-purple-500/50" />
                              <span className="text-gray-300 flex-1 truncate">{v.title}</span>
                              <span className="text-purple-300 font-medium">{v.impressions?.toLocaleString()} plays</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="p-8 text-center text-gray-400">
                  <Play className="h-10 w-10 mx-auto mb-3 opacity-30" />
                  <p>No custom ad impressions in the last {timeRange} days</p>
                </div>
              )}
            </div>

            {/* Google Ads — Per Video Impressions */}
            <div className="bg-gray-900/50 rounded-xl border border-gray-800 overflow-hidden">
              <div className="p-4 border-b border-gray-800 flex items-center justify-between">
                <div>
                  <h3 className="text-white font-semibold flex items-center gap-2">
                    <Globe className="h-5 w-5 text-blue-400" />
                    Google Ads (VAST/IMA) — Per Video Impressions
                  </h3>
                  <p className="text-xs text-gray-400 mt-1">How Google ads performed on each of your videos</p>
                </div>
                <button
                  onClick={resetGoogleAds}
                  data-testid="reset-google-ads-btn"
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-red-500/10 hover:bg-red-500/20 border border-red-500/30 rounded-lg text-red-400 text-xs font-medium transition-colors"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                  Reset Google Ads
                </button>
              </div>
              
              {myAdsData.google_ads?.videos?.length > 0 ? (
                <div className="divide-y divide-gray-800">
                  {myAdsData.google_ads.videos.map((v, idx) => (
                    <div key={v.video_id || idx} className="flex items-center gap-3 px-4 py-3">
                      {v.thumbnail_url ? (
                        <img src={v.thumbnail_url} alt="" className="w-12 h-8 object-cover rounded" />
                      ) : (
                        <div className="w-12 h-8 bg-blue-900/30 rounded flex items-center justify-center">
                          <Video className="h-3 w-3 text-blue-400" />
                        </div>
                      )}
                      <span className="text-gray-300 text-sm flex-1 truncate">{v.title}</span>
                      <span className="text-blue-300 text-sm font-medium">{v.impressions?.toLocaleString()} impressions</span>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="p-8 text-center text-gray-400">
                  <Globe className="h-10 w-10 mx-auto mb-3 opacity-30" />
                  <p>No Google ad impressions in the last {timeRange} days</p>
                </div>
              )}
            </div>

            {/* Daily Trend Charts */}
            {(myAdsData.my_ads?.daily_trend?.length > 0 || myAdsData.google_ads?.daily_trend?.length > 0) && (
              <div className="bg-gray-900/50 rounded-xl border border-gray-800 p-4">
                <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                  <ChartLine className="h-5 w-5 text-green-400" />
                  Daily Impressions Trend
                </h3>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={(() => {
                      const merged = {};
                      (myAdsData.my_ads?.daily_trend || []).forEach(d => {
                        if (!merged[d.date]) merged[d.date] = { date: d.date, myAds: 0, google: 0 };
                        merged[d.date].myAds = d.impressions;
                      });
                      (myAdsData.google_ads?.daily_trend || []).forEach(d => {
                        if (!merged[d.date]) merged[d.date] = { date: d.date, myAds: 0, google: 0 };
                        merged[d.date].google = d.impressions;
                      });
                      return Object.values(merged).sort((a, b) => a.date.localeCompare(b.date)).map(d => ({
                        ...d,
                        date: new Date(d.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
                      }));
                    })()}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                      <XAxis dataKey="date" tick={{ fill: '#9CA3AF', fontSize: 11 }} />
                      <YAxis tick={{ fill: '#9CA3AF', fontSize: 11 }} />
                      <Tooltip contentStyle={{ backgroundColor: '#1F2937', border: '1px solid #374151', borderRadius: '8px' }} />
                      <Area type="monotone" dataKey="myAds" name="My Ads" stackId="1" stroke="#a855f7" fill="#a855f7" fillOpacity={0.3} />
                      <Area type="monotone" dataKey="google" name="Google Ads" stackId="1" stroke="#3b82f6" fill="#3b82f6" fillOpacity={0.3} />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}
          </>
        )}
      </div>
      )}
    </div>
  );
};

export default RevenueTab;
